import type { ReactNode } from 'react';
import {
  CheckCircleFilled,
  ClusterOutlined,
  EditOutlined,
  FileTextOutlined,
  InboxOutlined,
  MergeCellsOutlined,
  CloseCircleFilled,
  PauseCircleFilled,
  PlayCircleFilled,
  PlusCircleFilled,
  ReloadOutlined,
  SwapOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';

export type TicketEventVisual = {
  color: string;
  icon: ReactNode;
};

/**
 * Compact colored icons for timeline titles (not used as Timeline.dot —
 * antd custom dots clip badly).
 */
export function getTicketEventVisual(
  eventType: string,
  executionResult?: string | null,
  toStatus?: string | null,
  reason?: string | null,
): TicketEventVisual {
  const size = { fontSize: 16 };
  if (
    eventType === 'STATUS_CHANGED' &&
    (toStatus === 'ON_HOLD' || Boolean(reason?.trim()))
  ) {
    return {
      color: '#8b5cf6',
      icon: <PauseCircleFilled style={{ ...size, color: '#8b5cf6' }} />,
    };
  }
  if (eventType === 'STATUS_CHANGED' && toStatus === 'IN_PROGRESS') {
    return {
      color: '#10b981',
      icon: <PlayCircleFilled style={{ ...size, color: '#10b981' }} />,
    };
  }
  switch (eventType) {
    case 'CREATED':
      return {
        color: '#2563eb',
        icon: <PlusCircleFilled style={{ ...size, color: '#2563eb' }} />,
      };
    case 'ASSIGNED':
      return {
        color: '#059669',
        icon: <UserAddOutlined style={{ ...size, color: '#059669' }} />,
      };
    case 'UNASSIGNED':
      return {
        color: '#d97706',
        icon: <UserDeleteOutlined style={{ ...size, color: '#d97706' }} />,
      };
    case 'BUNDLED':
      return {
        color: '#7c3aed',
        icon: <ClusterOutlined style={{ ...size, color: '#7c3aed' }} />,
      };
    case 'UNBUNDLED':
      return {
        color: '#9333ea',
        icon: <ClusterOutlined style={{ ...size, color: '#9333ea' }} />,
      };
    case 'MOVED':
      return {
        color: '#0284c7',
        icon: <SwapOutlined style={{ ...size, color: '#0284c7' }} />,
      };
    case 'STARTED':
      return {
        color: '#10b981',
        icon: <PlayCircleFilled style={{ ...size, color: '#10b981' }} />,
      };
    case 'REPORT_SUBMITTED':
      return {
        color: '#4f46e5',
        icon: <FileTextOutlined style={{ ...size, color: '#4f46e5' }} />,
      };
    case 'COMPLETED': {
      if (executionResult === 'COMPLETED_WITH_ISSUE') {
        return {
          color: '#d97706',
          icon: <CheckCircleFilled style={{ ...size, color: '#d97706' }} />,
        };
      }
      if (executionResult === 'NOT_DONE') {
        return {
          color: '#64748b',
          icon: <CheckCircleFilled style={{ ...size, color: '#64748b' }} />,
        };
      }
      if (executionResult === 'CANCELLED') {
        return {
          color: '#dc2626',
          icon: <CloseCircleFilled style={{ ...size, color: '#dc2626' }} />,
        };
      }
      return {
        color: '#16a34a',
        icon: <CheckCircleFilled style={{ ...size, color: '#16a34a' }} />,
      };
    }
    case 'WO_COMPLETED':
      return {
        color: '#16a34a',
        icon: <CheckCircleFilled style={{ ...size, color: '#16a34a' }} />,
      };
    case 'CANCELLED':
      return {
        color: '#dc2626',
        icon: <CloseCircleFilled style={{ ...size, color: '#dc2626' }} />,
      };
    case 'ARCHIVED':
      return {
        color: '#64748b',
        icon: <InboxOutlined style={{ ...size, color: '#64748b' }} />,
      };
    case 'REOPENED':
      return {
        color: '#ea580c',
        icon: <ReloadOutlined style={{ ...size, color: '#ea580c' }} />,
      };
    case 'STATUS_CHANGED':
      return {
        color: '#475569',
        icon: <EditOutlined style={{ ...size, color: '#475569' }} />,
      };
    case 'WO_MERGED':
      return {
        color: '#0d9488',
        icon: <MergeCellsOutlined style={{ ...size, color: '#0d9488' }} />,
      };
    default:
      return {
        color: '#94a3b8',
        icon: <EditOutlined style={{ ...size, color: '#94a3b8' }} />,
      };
  }
}

export function formatTicketEventWhen(
  value: Date,
  locale: string | undefined,
): string {
  return value.toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function shortActorId(actorId: string): string {
  if (actorId.length <= 12) return actorId;
  return `${actorId.slice(0, 8)}…`;
}

/**
 * WO-level noise on a ticket timeline (completion/merge of the container).
 * Ticket visit outcome is already captured by COMPLETED + executionResult.
 */
const TICKET_TIMELINE_EXCLUDED_EVENT_TYPES = new Set([
  'WO_COMPLETED',
  'WO_MERGED',
]);

export function isTicketTimelineEvent(eventType: string): boolean {
  return !TICKET_TIMELINE_EXCLUDED_EVENT_TYPES.has(eventType);
}

export type TicketEventDetailFields = {
  eventType: string;
  reason?: string | null;
  workOrderCode?: string | null;
  fromWorkOrderCode?: string | null;
  toWorkOrderCode?: string | null;
  fromStatus?: string | null;
  toStatus?: string | null;
};

/** Hold is the only STATUS_CHANGED that always carries a reason today. */
export function isPutOnHoldEvent(event: {
  eventType: string;
  toStatus?: string | null;
  reason?: string | null;
}): boolean {
  if (event.eventType !== 'STATUS_CHANGED') return false;
  if (event.toStatus === 'ON_HOLD') return true;
  return Boolean(event.reason?.trim());
}

export function isResumedFromHoldEvent(event: {
  eventType: string;
  fromStatus?: string | null;
  toStatus?: string | null;
}): boolean {
  return (
    event.eventType === 'STATUS_CHANGED' &&
    event.fromStatus === 'ON_HOLD' &&
    event.toStatus === 'IN_PROGRESS'
  );
}

/**
 * Human-readable detail line for an event (move from→to, linked WO, reason).
 */
export function formatTicketEventDetail(
  event: TicketEventDetailFields,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  const from = event.fromWorkOrderCode ?? null;
  const to = event.toWorkOrderCode ?? null;
  const wo = event.workOrderCode ?? null;
  const reason =
    typeof event.reason === 'string' && event.reason.trim()
      ? event.reason.trim()
      : null;

  if (isPutOnHoldEvent(event)) {
    if (reason && wo) {
      return t('workOrders.ticketDetail.timeline.heldOnWorkOrderWithReason', {
        code: wo,
        reason,
      });
    }
    if (reason) {
      return t('workOrders.ticketDetail.timeline.heldWithReason', { reason });
    }
    if (wo) {
      return t('workOrders.ticketDetail.timeline.heldOnWorkOrder', {
        code: wo,
      });
    }
    return t('workOrders.ticketDetail.timeline.held');
  }

  if (isResumedFromHoldEvent(event)) {
    if (wo) {
      return t('workOrders.ticketDetail.timeline.resumedOnWorkOrder', {
        code: wo,
      });
    }
    return t('workOrders.ticketDetail.timeline.resumed');
  }

  if (
    (event.eventType === 'MOVED' || event.eventType === 'BUNDLED') &&
    from &&
    to
  ) {
    return t('workOrders.ticketDetail.timeline.movedFromTo', { from, to });
  }

  if (event.eventType === 'WO_MERGED' && from && to) {
    return t('workOrders.ticketDetail.timeline.mergedInto', { from, to });
  }

  if (event.eventType === 'ARCHIVED' && wo) {
    return t('workOrders.ticketDetail.timeline.archivedWo', { code: wo });
  }

  if (
    (event.eventType === 'CREATED' ||
      event.eventType === 'STARTED' ||
      event.eventType === 'COMPLETED' ||
      event.eventType === 'WO_COMPLETED' ||
      event.eventType === 'WO_CLOSED' ||
      event.eventType === 'ASSIGNED') &&
    wo
  ) {
    return t('workOrders.ticketDetail.timeline.onWorkOrder', { code: wo });
  }

  if (reason) return reason;
  if (wo)
    return t('workOrders.ticketDetail.timeline.onWorkOrder', { code: wo });
  return null;
}

export function ticketEventTitleKey(
  eventType: string,
  toStatus?: string | null,
  fromStatus?: string | null,
  reason?: string | null,
): string {
  if (isPutOnHoldEvent({ eventType, toStatus, reason })) {
    return 'workOrders.ticketDetail.timeline.eventPutOnHold';
  }
  if (isResumedFromHoldEvent({ eventType, fromStatus, toStatus })) {
    return 'workOrders.ticketDetail.timeline.eventResumed';
  }
  return `workOrders.eventType.${eventType}`;
}
